import { useState, useRef, useEffect, useId, useMemo, CSSProperties } from "react"
import { createPortal } from "react-dom"
import { Icon } from "../../atoms/Icon"
import { Text } from "../../atoms/Text"
import { Input } from "../Input"
import { cn } from "@/utils/cn"

export interface SearchableSelectOption {
  value: string
  label: string
}

export interface SearchableSelectProps {
  id?: string
  label?: string
  value: string
  options: SearchableSelectOption[]
  onValueChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  containerClassName?: string
}

export const SearchableSelect = ({
  id,
  label,
  value,
  options,
  onValueChange,
  placeholder = "Selecione...",
  disabled = false,
  containerClassName,
}: SearchableSelectProps) => {
  const generatedId = useId()
  const inputId = id ?? generatedId

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({})

  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const portalRef = useRef<HTMLDivElement>(null)

  const calcPosition = (): CSSProperties => {
    if (!triggerRef.current) return {}
    const rect = triggerRef.current.getBoundingClientRect()
    return {
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    }
  }

  useEffect(() => {
    if (!isOpen) return
    const update = () => setDropdownStyle(calcPosition())
    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [isOpen])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        !wrapperRef.current?.contains(target) &&
        !portalRef.current?.contains(target)
      ) {
        setIsOpen(false)
        setSearch("")
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false)
        setSearch("")
      }
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, search])

  const selectedLabel = options.find((o) => o.value === value)?.label

  const handleToggle = () => {
    if (disabled) return
    if (!isOpen) setDropdownStyle(calcPosition())
    setIsOpen((prev) => {
      if (prev) setSearch("")
      return !prev
    })
  }

  const handleSelect = (optionValue: string) => {
    onValueChange(optionValue)
    setIsOpen(false)
    setSearch("")
  }

  return (
    <div
      ref={wrapperRef}
      className={cn("flex w-full flex-col gap-2 relative", containerClassName)}
    >
      {label && (
        <Text as="label" variant="label" htmlFor={inputId}>
          {label}
        </Text>
      )}

      <button
        ref={triggerRef}
        id={inputId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={handleToggle}
        className={cn(
          "flex h-[clamp(40px,4vh,50px)] w-full items-center gap-[10px] rounded-[12px] bg-dark px-[20px] transition-all border border-transparent",
          "text-sm text-left",
          !selectedLabel && "text-dark-100/50",
          selectedLabel && "text-dark-100",
          disabled ? "opacity-50 cursor-not-allowed" : "hover:border-white/20 cursor-pointer"
        )}
      >
        <span className="flex-1 truncate">
          {selectedLabel ?? placeholder}
        </span>
        <Icon name={isOpen ? "angle-small-up" : "angle-small-down"} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={portalRef}
            style={dropdownStyle}
            className="rounded-[12px] bg-dark border border-white/10 shadow-lg"
          >
            <div className="p-2 border-b border-white/10">
              <Input
                id={`${inputId}-search`}
                iconLeft="search"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <ul className="max-h-52 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-4 py-2 text-sm text-white/40 select-none">
                  Nenhum resultado
                </li>
              ) : (
                filtered.map((option, idx) => {
                  const isSelected = value === option.value
                  return (
                    <li key={`${option.value || "__empty__"}-${idx}`}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleSelect(option.value)}
                        className={cn(
                          "flex w-full items-center gap-2 px-4 py-2 text-sm text-left transition-colors",
                          isSelected
                            ? "bg-white/15 text-white font-medium"
                            : "text-white/70 hover:bg-white/10"
                        )}
                      >
                        <span className="w-4 flex-shrink-0 flex items-center justify-center">
                          {isSelected && <Icon name="check" />}
                        </span>
                        <span className="truncate">{option.label}</span>
                      </button>
                    </li>
                  )
                })
              )}
            </ul>
          </div>,
          document.body
        )}
    </div>
  )
}
